import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setIsSubmitted(true);
    } catch (error) {
      if (!error.response) {
        // Network error — request never reached server
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.response.status === 429) {
        toast.error(error.response.data?.error || 'Too many requests. Please try again later.');
      } else if (error.response.status === 500) {
        // Email delivery failed — let the user know so they can retry
        toast.error(error.response.data?.error || 'Unable to send reset email. Please try again or contact IT support.');
      } else {
        // For 404, 400, etc. — don't reveal whether email exists
        setIsSubmitted(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - branding */}
        <div className="hidden lg:flex lg:flex-1 bg-wrg-blue-500 items-center justify-center p-12">
          <div className="max-w-md text-center">
            <h1 className="text-4xl font-semibold text-white mb-4">
              WRG Travel
            </h1>
            <p className="text-xl text-blue-200 mb-8">
              World Racing Group Travel Request System
            </p>
          </div>
        </div>

        {/* Right side - success message */}
        <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
          <div className="w-full max-w-md">
            <div className="lg:hidden text-center mb-8">
              <h1 className="text-3xl font-semibold text-wrg-blue-500">WRG Travel</h1>
              <p className="text-gray-600 mt-2">Travel Request System</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center">
                <div className="size-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="size-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Check your email</h2>
                <p className="text-gray-600 mb-6">
                  If an account exists with that email address, you will receive a password reset link shortly.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  The link will expire in 1 hour. Don't forget to check your spam folder.
                </p>
                <Link
                  to="/login"
                  className="inline-block w-full py-2.5 px-4 bg-wrg-blue-500 text-white font-medium rounded-lg hover:bg-wrg-blue-700 focus:ring-4 focus:ring-wrg-blue-500/50 transition-colors text-center"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:flex-1 bg-wrg-blue-500 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <h1 className="text-4xl font-semibold text-white mb-4">
            WRG Travel
          </h1>
          <p className="text-xl text-blue-200 mb-8">
            World Racing Group Travel Request System
          </p>
        </div>
      </div>

      {/* Right side - forgot password form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-semibold text-wrg-blue-500">WRG Travel</h1>
            <p className="text-gray-600 mt-2">Travel Request System</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Forgot Password?</h2>
            <p className="text-gray-600 mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wrg-blue-500 focus:border-transparent transition-colors"
                  placeholder="name@dirtcar.com"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 px-4 bg-wrg-blue-500 text-white font-medium rounded-lg hover:bg-wrg-blue-700 focus:ring-4 focus:ring-wrg-blue-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin size-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending…
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-wrg-blue-500 hover:text-wrg-blue-600 font-medium"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
